<script lang="ts" setup>
import { resolveMmLogo } from '#public-research/utils/pr.utils'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Card } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'

const PLACEHOLDER_COUNT = 4
const PLACEHOLDER_LOGO = resolveMmLogo(null)
const PLACEHOLDER_ROWS = Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => ({
  key: `placeholder-${i}`,
  placeholder: true as const,
  label: '',
  marketMakerName: '',
  logoSrc: PLACEHOLDER_LOGO,
  value: null as string | null,
}))

const { items, isLoading } = useLeaderboardSummaryCards('index')

const rows = computed(() => {
  if (isLoading.value) {
    return PLACEHOLDER_ROWS
  }
  const mapped = items.value.slice(0, PLACEHOLDER_COUNT).map(item => ({
    key: `${item.label}-${item.marketMakerName}`,
    placeholder: false as const,
    label: item.label,
    marketMakerName: item.marketMakerName,
    logoSrc: resolveMmLogo(item.marketMakerImageUrl) as string | null,
    value: item.value ?? null,
  }))
  while (mapped.length < PLACEHOLDER_COUNT) {
    mapped.push({
      key: `empty-${mapped.length}`,
      placeholder: false as const,
      label: '',
      marketMakerName: '',
      logoSrc: null,
      value: null,
    })
  }
  return mapped
})
</script>

<template>
  <Card class="grid grid-cols-1 divide-y divide-default border-default sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4">
    <div
      v-for="(row, i) in rows"
      :key="row.key"
      class="flex flex-col gap-2 p-4"
      :class="{
        'sm:border-r sm:border-default': i % 2 === 0,
        'lg:border-r lg:border-default': i < rows.length - 1,
        'sm:border-b sm:border-default lg:border-b-0': i < 2,
      }"
    >
      <Skeleton v-if="row.placeholder" class="h-4 w-32" />
      <span v-else class="truncate text-2xs text-secondary">{{ row.label }}</span>
      <div class="flex items-center gap-2">
        <template v-if="row.placeholder">
          <Skeleton class="size-5 shrink-0 rounded-full" />
          <Skeleton class="h-5 flex-1" />
          <Skeleton class="h-5 w-14 shrink-0" />
        </template>
        <template v-else>
          <Avatar class="size-5 shrink-0 border border-default bg-white">
            <AvatarImage v-if="row.logoSrc" :src="row.logoSrc" :alt="row.marketMakerName" />
            <AvatarFallback class="text-2xs">
              {{ row.marketMakerName.slice(0, 1) }}
            </AvatarFallback>
          </Avatar>
          <span class="min-w-0 flex-1 truncate text-sm font-semibold text-primal">{{ row.marketMakerName }}</span>
          <span class="shrink-0 text-sm font-semibold text-primal">{{ row.value ?? 'N/A' }}</span>
        </template>
      </div>
    </div>
  </Card>
</template>
